import { Skeleton } from 'primereact/skeleton';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import EmptyScreen from '../../../../../../components/empty-screen/EmptyScreen';
import PcfTable from '../../../../../../components/pcf-table/PcfTable';
import { useGetFootprintsByComponentId } from '../../../../../../hooks/api/footprints';
import { IFootPrintTableRow } from '../../../../../../shared/interfaces/IFootprint';
import FootprintDetailsPage from '../../../../../footprint-details/FootprintDetailsPage';

const PcfTab = (): JSX.Element => {
  // Read params from URL
  const { componentId, footprintId } = useParams<{ componentId: string; footprintId: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: footprints, isLoading } = useGetFootprintsByComponentId(componentId || '', {
    enabled: !!componentId,
  });

  if (footprintId) {
    return <FootprintDetailsPage />;
  }

  const renderLoadingContent = (): JSX.Element => {
    return (
      <div>
        <Skeleton
          height='15rem'
          className='flex w-full flex-row justify-content-center mb-3 border-round-xl'
        />
        <Skeleton height='2.5rem' width='10rem' className='border-round-lg' />
      </div>
    );
  };

  const emptyScreenContent = (
    <EmptyScreen>
      <p className='flex text-center font-light'>
        {t('componentDetailsPage.tabs.pcfTab.emptyScreenText')}
      </p>
    </EmptyScreen>
  );

  const pcfTableContent = (
    <div>
      <h1 className='text-xl md:text-2xl font-medium p-0 my-3'>
        {t('productDetailsPage.tabs.pcfTab.pcfHeader')}
      </h1>
      <div className='flex flex-column gap-4'>
        <PcfTable
          footprints={footprints || []}
          isLoading={isLoading}
          onRowClick={(rowData: IFootPrintTableRow) => {
            const href = `/components/${componentId}/pcf/footprint/${rowData.id}`;
            navigate(href);
          }}
        />
      </div>
    </div>
  );

  // Main content entry-point
  // Determine the content based on viewMode
  const renderPcfTabContent = (): JSX.Element => {
    // If there are footprints, show the table content
    if (footprints?.length) {
      return pcfTableContent;
    }

    // Otherwise, show the empty screen content
    return emptyScreenContent;
  };

  return isLoading ? renderLoadingContent() : renderPcfTabContent();
};

export default PcfTab;
